import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet";


const PrivacyPolicy = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy - CrikStars</title>
            </Helmet>
            <div className="p-4 max-w-5xl mx-auto">
                <h1 className="text-2xl md:text-3xl font-bold mb-4">Privacy Policy – CrikStars</h1>

                <p className="mb-4">
                    <b className="font-semibold">Last Updated:</b> 2 December 2025
                </p>

                <p className="mb-6 text-gray-700 leading-relaxed">
                    CrikStars ("we", "our", "us") provides a cricket scoring and tournament management application for
                    players, scorers, teams, and organizers. We respect your privacy and are committed to protecting your
                    personal data. This Privacy Policy explains how we collect, use, store, and protect your information in
                    compliance with Apple App Store Review Guidelines.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">1. Information We Collect</h2>

                <h3 className="font-semibold mt-4 mb-2 text-lg text-gray-800">a. Personal Information</h3>
                <ul className="list-disc list-inside ml-4 mb-4 space-y-1 text-gray-700">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Profile photo (optional)</li>
                </ul>

                <h3 className="font-semibold mt-4 mb-2 text-lg text-gray-800">b. Usage & Cricket Data</h3>
                <ul className="list-disc list-inside ml-4 mb-4 space-y-1 text-gray-700">
                    <li>Match scoring data</li>
                    <li>Tournaments created</li>
                    <li>Player and team details</li>
                    <li>Match settings and visibility preferences</li>
                    <li>In-app interactions and activity logs</li>
                </ul>

                <h3 className="font-semibold mt-4 mb-3 text-lg text-gray-800">c. Permissions & Device Information</h3>

                <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
                    <p className="font-semibold mb-2 text-gray-900">Location Access</p>
                    <p className="mb-2 text-gray-700 leading-relaxed">
                        CrikStars requests <b className="font-semibold"> one-time location access only while creating a match </b> to tag the correct ground
                        location.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                        We do not: - Access location in the background - Track user movement - Store continuous or historical
                        location data
                    </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
                    <p className="font-semibold mb-2 text-gray-900">OTP Auto-Fill (iOS)</p>
                    <p className="mb-2 text-gray-700 leading-relaxed">
                        On iOS, OTP may be suggested by the system keyboard for user convenience.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                        CrikStars does not: - Read SMS messages - Access personal messages - Store OTP messages
                    </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
                    <p className="font-semibold mb-2 text-gray-900">WhatsApp OTP Delivery</p>
                    <p className="mb-2 text-gray-700 leading-relaxed">
                        If enabled, login OTP may be delivered via WhatsApp using official WhatsApp Business APIs.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                        We do not: - Read WhatsApp messages - Collect WhatsApp chat data - Store WhatsApp information
                    </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
                    <p className="font-semibold mb-2 text-gray-900">Device & Diagnostic Data</p>
                    <ul className="list-disc list-inside ml-4 mb-2 space-y-1 text-gray-700">
                        <li>Device model</li>
                        <li>Operating system version</li>
                        <li>Crash reports</li>
                        <li>App usage analytics</li>
                    </ul>
                    <p className="text-gray-700">This data is used only to improve app performance and stability.</p>
                </div>

                <h3 className="font-semibold mt-4 mb-2 text-lg text-gray-800">d. Public Match Visibility</h3>
                <p className="mb-2 text-gray-700 leading-relaxed">
                    Live and completed matches are visible to CrikStars users within the app, including: - Scores - Overs -
                    Player statistics
                </p>
                <p className="mb-2 text-gray-700 leading-relaxed">
                    We never display publicly: - Phone numbers - Email addresses - Private profile information
                </p>
                <p className="mb-6 text-gray-700 leading-relaxed">Only match-related cricket data is visible.</p>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">2. How We Use Your Information</h2>
                <p className="mb-2 text-gray-700 leading-relaxed">
                    We use your information to: - Enable match scoring and tournament management features - Provide
                    secure login and OTP verification - Tag matches with accurate ground locations - Deliver optional
                    WhatsApp OTP services - Sync offline scoring data - Provide customer support - Improve app
                    performance, reliability, and user experience - Display public match scorecards within CrikStars
                </p>
                <p className="mt-4 mb-6 text-gray-700 leading-relaxed">We do not sell, rent, or trade your personal data.</p>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">3. Sharing of Information</h2>
                <p className="mb-2 text-gray-700 leading-relaxed">
                    We share data only with: - Trusted service providers (hosting, analytics, crash reporting) - Legal
                    authorities when required by law - Other app users only in the context of public match data (scores and
                    statistics)
                </p>
                <p className="mb-6 text-gray-700 leading-relaxed">Personal identification data is never shared publicly.</p>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">4. Data Storage & Security</h2>
                <ul className="list-disc list-inside ml-4 mb-6 space-y-1 text-gray-700">
                    <li>All communication is encrypted using HTTPS</li>
                    <li>Sensitive data is stored securely</li>
                    <li>Offline scoring data is stored locally on the device until synced</li>
                    <li>Access to user data is restricted and protected</li>
                </ul>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">5. Data Retention</h2>
                <p className="mb-2 text-gray-700 leading-relaxed">
                    We retain user data only for as long as necessary to provide app functionality.
                </p>
                <ul className="list-disc list-inside ml-4 mb-6 space-y-1 text-gray-700">
                    <li>
                        Upon account deletion, personal data is permanently removed within 30 days
                    </li>
                    <li>
                        Certain data may be retained longer if legally required
                    </li>
                </ul>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">6. Your Rights & Account Deletion</h2>
                <p className="mb-4 text-gray-700 leading-relaxed">
                    You have the right to: - Access your personal data - Correct inaccurate information - Permanently delete
                    your account and associated data
                </p>

                <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
                    <p className="font-semibold mb-2 text-gray-900">Account Deletion Options</p>
                    <p className="leading-relaxed">
                        Users can delete their account: - Within the app: Profile → Delete Account - Via web:{' '}
                        <Link to="/delete-account" className="text-primary-600 font-semibold hover:text-primary-700">
                            https://crikstars.com/delete-account
                        </Link>{' '}
                    </p>
                    <p>
                        By email:{' '}
                        <Link to="mailto:support@crikstars.com" className="text-primary-600 font-semibold hover:text-primary-700 underline">
                            support@crikstars.com
                        </Link>
                    </p>
                </div>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">7. Children's Privacy</h2>
                <p className="mb-6 text-gray-700 leading-relaxed">
                    CrikStars is not intended for children under the age of 13. We do not knowingly collect personal data
                    from children.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">8. Updates to This Policy</h2>
                <p className="mb-6 text-gray-700 leading-relaxed">
                    We may update this Privacy Policy from time to time. Any changes will be posted with an updated
                    revision date.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4 text-gray-900">9. Contact Us</h2>
                <p className="mb-2 text-gray-700 leading-relaxed">
                    If you have any questions or requests regarding this Privacy Policy:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-gray-700 leading-relaxed">
                        Email:{' '}
                        <Link to="mailto:support@crikstars.com" className="text-primary-600 font-semibold hover:text-primary-700 underline">
                            support@crikstars.com
                        </Link>
                        <br />
                        Website:{' '}
                        <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 underline">
                            https://crikstars.com
                        </Link>
                    </p>
                </div>
            </div>
        </>
    );
};

export default PrivacyPolicy;